import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../Appstore/store";
import { AddSmsCampaignsList } from "../Features/smscampaignlist/smscampaignlist";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { IoDownloadOutline } from "react-icons/io5";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface FileUploadProps {
  field: any;
  form: any;
  label: string;
}

interface SmsCampaignListAdd {
  name: string;
  numbers: File[];
  message: string;
  id?: number;
  retries: number;
  schedule_time: string | null;
  retry_minutes: number
}

const Addsmscampaignlist: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { id }: any = useParams();

  const isActive = (path: string) =>
    location.pathname === path ? "active" : "";

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required").trim(),
    numbers: Yup.array()
      .min(1, "At least one file is required")
      .test("fileType", "Only .xlsx and .xls files are allowed", (files) => {
        return files?.every(
          (file) => file.name.endsWith(".xlsx") || file.name.endsWith(".xls")
        );
      }),
    is_schedule: Yup.boolean(),
    message: Yup.string().required("Message is required").trim(),
    scheduletime: Yup.string().when("is_schedule", {
      is: true,
      then: (schema) => Yup.string().required("Schedule time is required"),
    }),
    retries: Yup.number()
      .required("Retries is required")
      .min(0, "Retries must be at least 0"),
    retry_minutes: Yup.number()
      .min(1)
      .max(60)
      .required()
      .label("Retry minutes"),
  });

  const initialValues = {
    name: "",
    numbers: [],
    message: "",
    scheduletime: "",
    retries: "",
    is_schedule: false,
    retry_minutes: "",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };

  const formatDateTimeTo12Hour = (datetimeValue: string) => {
    const [date, time] = datetimeValue.split("T");
    let [hours, minutes] = time.split(":");

    const isPM = Number(hours) >= 12;
    const ampm = isPM ? "PM" : "AM";
    hours = String(Number(hours) % 12 || 12).padStart(2, "0");

    return `${date} ${hours}:${minutes}:00 ${ampm}`;
  };

  const FileUpload: React.FC<FileUploadProps> = ({ field, form, label }) => {
    const { setFieldValue } = form;

    const onDrop = (acceptedFiles: File[]) => {
      setFieldValue(field.name, acceptedFiles);
    };

    const handleRemoveFile = (index: number) => {
      const newFiles = field.value.filter((_: File, i: number) => i !== index);
      setFieldValue(field.name, newFiles);
    };

    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
        "application/vnd.ms-excel": [".xls"],
      },
      multiple: false,
    });

    return (
      <>
        <label htmlFor="name">
          Numbers (Upload Excel File)
          <span className="text-danger">*</span>
        </label>
        <div className="d-flex gap-2 justify-content-center align-items-center form-control ">
          <div {...getRootProps()} className="dropzone flex-grow-1">
            <input {...getInputProps()} />
            <p
              className={`shadow-none mb-0 ${form.touched.numbers && form.errors.numbers ? "is-invalid" : ""
                }`}
            >
              Upload or drop a file right here
            </p>
          </div>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>sample file</Tooltip>}
          >
            <a
              type="button"
              rel="noreferrer"
              href={
                process.env.REACT_APP_BASE_PATH +
                "/assets/sample/SMS-sample-file.xlsx"
              }
              target="_blank"
              download="SMS-sample-file"
            >
              <IoDownloadOutline
                size={22}
                data-bs-title="This top tooltip is themed via CSS variables."
                data-bs-custom-class="custom-tooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
              />
            </a>
          </OverlayTrigger>
        </div>
        {field.value && field.value.length > 0 && (
          <div className="d-flex flex-wrap">
            {field.value.map((file: File, index: number) => (
              <div
                key={index}
                className="d-flex align-items-center mb-3 border p-2 mt-2"
              >
                <p className="mb-0">{file.name}</p>
                <span className="ms-2" onClick={() => handleRemoveFile(index)}>
                  <i
                    className="fa-solid fa-square-xmark text-danger"
                    style={{ cursor: "pointer" }}
                  ></i>
                </span>
              </div>
            ))}
          </div>
        )}
        {form.touched.numbers && form.errors.numbers && (
          <div className="text-danger">{form.errors.numbers}</div>
        )}
      </>
    );
  };

  return (
    <>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">Add SMS Campaign List</h2>
                  <Link to={""} className="text-decoration-none">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                      const SmsCampaignListData: SmsCampaignListAdd = {
                        name: values.name,
                        numbers: values.numbers,
                        message: values.message,
                        schedule_time: values.scheduletime
                          ? formatDateTimeTo12Hour(values.scheduletime)
                          : null,
                        retries: Number(values.retries),
                        retry_minutes: Number(values?.retry_minutes)
                      };

                      try {
                        const response: any = await dispatch(
                          AddSmsCampaignsList({
                            sms_campaign_id: id ?? 0,
                            addData: SmsCampaignListData,
                          })
                        );
                        if (
                          response.payload &&
                          response.payload.status === "success"
                        ) {
                          toast.success(response.payload.message);
                          navigate(
                            `/admin/sms-campaigns/view/sms-campaigns-list/${id}/list`
                          );
                        } else {
                          toast.error(response.payload.message);
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                      setFieldValue,
                    }) => (
                      <Form className="row">
                        <div className="col-md-6">
                          <div className="form-group mb-3 ">
                            <label htmlFor="name">
                              Name
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.name && errors.name ? "is-invalid" : ""
                                }`}
                              id="name"
                              name="name"
                              placeholder="Enter a Name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.name && errors.name && (
                              <div className="text-danger">{errors.name}</div>
                            )}
                          </div>

                          <div className="row">
                            <div className="col-6">
                              <div className="form-group mb-3">
                                <label htmlFor="retries">
                                  Retries<span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  className={`form-control shadow-none ${touched.retries && errors.retries
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  id="retries"
                                  name="retries"
                                  placeholder="Enter a Retries Number"
                                  value={values.retries}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.retries && errors.retries && (
                                  <div className="text-danger">
                                    {errors.retries}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="form-group mb-3 ">
                                <label htmlFor="retry_minutes">
                                  Retry Minutes
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="number"
                                  className={`form-control shadow-none ${touched.retry_minutes &&
                                    errors.retry_minutes
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  id="retry_minutes"
                                  name="retry_minutes"
                                  placeholder="Enter a retry minutes"
                                  value={values.retry_minutes}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                                {touched.retry_minutes &&
                                  errors.retry_minutes && (
                                    <div className="text-danger">
                                      {errors.retry_minutes}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>

                          <div className="form-group mb-3">
                            <Field name="numbers" component={FileUpload} />
                          </div>
                        </div>
                        <div className="form-group mb-3 col-md-6">
                          <label htmlFor="message">
                            Message<span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control shadow-none ${touched.message && errors.message
                              ? "is-invalid"
                              : ""
                              }`}
                            id="message"
                            name="message"
                            placeholder="Enter a Message"
                            style={{ resize: "none", height: 210 }}
                            value={values.message}
                            onChange={(e) =>
                              setFieldValue(
                                "message",
                                e.target.value?.replaceAll("$", "")
                              )
                            }
                            onBlur={handleBlur}
                          />
                          <small>
                            <b>Note:</b> Use square brackets "[]" to insert the
                            corresponding value from the XLSX file. For example,
                            [Firstname].
                          </small>
                          {touched.message && errors.message && (
                            <div className="text-danger">{errors.message}</div>
                          )}
                        </div>
                        <div className="form-group mb-3 col-md-6">
                          <div className="d-flex gap-2 user-select-none">
                            <input
                              type="checkbox"
                              checked={values.is_schedule}
                              onChange={(e) =>
                                setFieldValue("is_schedule", e.target.checked)
                              }
                              className="mb-2"
                              id="show_schedule"
                            />
                            <label htmlFor="show_schedule">
                              Schedule Campaign ?
                            </label>
                          </div>

                          {values.is_schedule ? (
                            <input
                              type="datetime-local"
                              className={`form-control shadow-none ${touched.scheduletime && errors.scheduletime
                                ? "is-invalid"
                                : ""
                                }`}
                              id="scheduletime"
                              name="scheduletime"
                              value={values.scheduletime}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          ) : null}

                          {touched.scheduletime && errors.scheduletime && (
                            <div className="text-danger">
                              {errors.scheduletime}
                            </div>
                          )}
                        </div>

                        <div className="d-flex">
                          <button
                            type="submit"
                            className="btn btn-primary me-2"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ps-2">Submitting...</span>
                              </>
                            ) : (
                              "Submit"
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)}
                          >
                            Cancel
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Addsmscampaignlist;
